import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccountMembership, APIService, CreateCustomPropertyDefinitionsInput, CustomPropertyDefinitions, UpdateCustomPropertyDefinitionsInput } from '../../../../API.service.custom';
import { ToastrService } from 'ngx-toastr';
import { MatButton } from '@angular/material/button';
import { AiDealStage } from '../ai-workspace-setup.models';
import { fetchAllPropertiesByAccountAndType } from 'app/shared/pagination/paginated-api-queries';
import { stringToUppercaseUnderscore } from '../../../../../globals/functions';

@Component({
  selector: 'ws-deal-stages',
  templateUrl: './ws-deal-stages.component.html',
  imports: [FormsModule, NgClass, MatButton, DragDropModule]
})
export class WsDealStagesComponent implements OnInit, OnChanges {
  @Input() suggestions: AiDealStage[];
  @Input() activeAccount: AccountMembership;
  @Input() testMode = false;
  @Output() completed = new EventEmitter<void>();
  @Output() feedbackRequested = new EventEmitter<string>();

  stages: AiDealStage[] = [];
  isSaving = false;
  existingProperty: CustomPropertyDefinitions = null;
  feedback = '';
  newStageName = '';

  constructor(
    private api: APIService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.initStages();
    if (!this.testMode) this.checkExistingData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions'] && this.suggestions) {
      this.initStages();
    }
  }

  private initStages() {
    this.stages = (this.suggestions || []).map(s => ({
      ...s,
      value: s.value || stringToUppercaseUnderscore(s.name),
      selected: s.selected !== false,
    }));

    // Auto-fix: ensure only one terminal success and one terminal failure
    let foundWon = false;
    let foundLost = false;
    for (const stage of this.stages) {
      if (stage.isTerminalSuccess) {
        if (foundWon) { stage.isTerminalSuccess = false; }
        else { foundWon = true; }
      }
      if (stage.isTerminalFailure) {
        if (foundLost) { stage.isTerminalFailure = false; }
        else { foundLost = true; }
      }
    }
  }

  private async checkExistingData() {
    if (!this.activeAccount?.accountId) return;
    try {
      const properties = await fetchAllPropertiesByAccountAndType(
        this.api,
        this.activeAccount.accountId
      );
      if (properties) {
        const existing = properties.find((p: any) => p.propertyName === 'dealStage');
        if (existing) {
          this.existingProperty = existing as CustomPropertyDefinitions;
          if (existing.possibleValues?.length) {
            this.stages = existing.possibleValues.map((pv: any) => {
              const attrs = pv.otherAttributes ? JSON.parse(pv.otherAttributes) : {};
              return {
                name: attrs.name || pv.viewValue || pv.value,
                value: pv.value,
                description: attrs.description || '',
                isTerminalSuccess: attrs.isTerminalStateInSuccess || false,
                isTerminalFailure: attrs.isTerminalStateInFailure || false,
                selected: true,
              } as AiDealStage;
            });
          }
        }
      }
    } catch (e) {
      console.error('Error checking existing deal stages:', e);
    }
  }

  toggleStage(stage: AiDealStage) {
    stage.selected = !stage.selected;
  }

  removeStage(stage: AiDealStage) {
    this.stages = this.stages.filter(s => s !== stage);
  }

  addStage() {
    if (!this.newStageName.trim()) return;
    this.stages.push({
      name: this.newStageName.trim(),
      value: stringToUppercaseUnderscore(this.newStageName.trim()),
      description: '',
      isTerminalSuccess: false,
      isTerminalFailure: false,
      selected: true,
    });
    this.newStageName = '';
  }

  reorderStages(event: CdkDragDrop<AiDealStage[]>) {
    moveItemInArray(this.stages, event.previousIndex, event.currentIndex);
  }

  setTerminalWin(stage: AiDealStage) {
    this.stages.forEach(s => s.isTerminalSuccess = false);
    stage.isTerminalSuccess = true;
    stage.isTerminalFailure = false;
  }

  setTerminalLoss(stage: AiDealStage) {
    this.stages.forEach(s => s.isTerminalFailure = false);
    stage.isTerminalFailure = true;
    stage.isTerminalSuccess = false;
  }

  getCardClass(stage: AiDealStage): string {
    if (stage.isTerminalSuccess) return 'ws-card-positive';
    if (stage.isTerminalFailure) return 'ws-card-negative';
    return '';
  }

  async save() {
    if (this.isSaving) return;
    const selected = this.stages.filter(s => s.selected);
    if (selected.length === 0) {
      this.toastr.warning('Please select at least one deal stage');
      return;
    }

    const winStages = selected.filter(s => s.isTerminalSuccess);
    const lossStages = selected.filter(s => s.isTerminalFailure);

    if (winStages.length === 0) {
      this.toastr.warning('Please mark one stage as "Won" (terminal success)');
      return;
    }
    if (lossStages.length === 0) {
      this.toastr.warning('Please mark one stage as "Lost" (terminal failure)');
      return;
    }
    if (winStages.length > 1) {
      this.toastr.warning('Only one stage can be marked as "Won"');
      return;
    }
    if (lossStages.length > 1) {
      this.toastr.warning('Only one stage can be marked as "Lost"');
      return;
    }

    if (this.testMode) {
      this.toastr.success('Test mode: Deal stages saved');
      this.completed.emit();
      return;
    }

    this.isSaving = true;

    try {
      const possibleValues = selected.map(s => ({
        value: s.value || stringToUppercaseUnderscore(s.name),
        viewValue: s.name.trim(),
        otherAttributes: JSON.stringify({
          name: s.name.trim(),
          description: s.description?.trim() || '',
          isTerminalStateInSuccess: s.isTerminalSuccess || false,
          isTerminalStateInFailure: s.isTerminalFailure || false,
        }),
      }));

      if (this.existingProperty) {
        const input = <UpdateCustomPropertyDefinitionsInput>{
          id: this.existingProperty.id,
          accountId: this.existingProperty.accountId,
          possibleValues,
          updaedById: this.activeAccount.userId,
        };
        await this.api.UpdateCustomPropertyDefinitions(input);
      } else {
        const input = <CreateCustomPropertyDefinitionsInput>{
          accountId: this.activeAccount.accountId,
          propertyName: 'dealStage',
          displayName: 'Deal Stage',
          description: 'Deal Stage',
          scalarType: 'Option',
          isSystemProperty: true,
          parentObjectType: 'Contact',
          possibleValues,
          createdById: this.activeAccount.userId,
          updaedById: this.activeAccount.userId,
        };
        await this.api.CreateCustomPropertyDefinitions(input);
      }

      this.toastr.success('Deal stages saved');
      this.completed.emit();
    } catch (e: any) {
      console.error('Error saving deal stages:', e);
      this.toastr.error(e?.errors?.[0]?.message || 'Failed to save');
    } finally {
      this.isSaving = false;
    }
  }

  submitFeedback() {
    if (this.feedback.trim()) {
      this.feedbackRequested.emit(this.feedback.trim());
      this.feedback = '';
    }
  }
}
